import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import IconButton from '@payaca/components/button/IconButton';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import RadioButton from '@payaca/components/radioButton/RadioButton';
import TypeToSearchField, {
  TypeToSearchRenderOptionProps,
} from '@payaca/components/typeToSearchField/TypeToSearchField';
import CustomerInformation from '../customerInformation/CustomerInformation';
import SelectCustomerModal from '../selectCustomerModal/SelectCustomerModal';
import EditJobSection from './EditJobSection';

import { getPrimaryContactFromCustomer } from '@payaca/helpers/customerHelper';

import * as customerActions from '@payaca/store/customer/customerActions';
import { CustomerContact } from '@payaca/types/customerTypes';

import { getCustomer } from '@/utils/stateAccessors';
import {
  GetListedCustomersRequestData,
  ListedCustomer,
} from '@payaca/types/listedCustomerTypes';

import { useSelector } from '@/api/state';
import UntitledIcon from '@payaca/untitled-icons';
import { clsx } from '@payaca/utilities/cvaMerge';
import './JobCustomerControl.sass';

interface Option {
  id: number;
  name: string;
  primaryContactEmailAddress?: string;
}

type Props = {
  canSelectCustomer?: boolean;
  customerId?: number;
  jobContactId?: number | null;
  onPersistCustomerSuccess?: () => void;
  onSelectContact?: (customerContactId: number) => void;
  onSelectCustomer?: (customerId?: number) => void;
};

const JobCustomerControl: FunctionComponent<Props> = ({
  canSelectCustomer = true,
  customerId,
  jobContactId,
  onPersistCustomerSuccess,
  onSelectContact,
  onSelectCustomer,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [showSearchCustomerTableModal, setShowSearchCustomerTableModal] =
    useState(false);

  const [getListedCustomersRequestData, setGetListedCustomersRequestData] =
    useState<GetListedCustomersRequestData>({
      pageSize: 20,
      pageNumber: 1,
      searchTerm: '',
    });

  useEffect(() => {
    requestGetListedCustomersPage();
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'requestGetListedCustomersPage'
  }, [getListedCustomersRequestData]);

  const requestGetListedCustomersPage = useCallback(() => {
    dispatch(
      customerActions.requestGetListedCustomersPage(
        getListedCustomersRequestData
      )
    );
  }, [getListedCustomersRequestData, dispatch]);

  const listedCustomersPage = useSelector((state) => {
    return state.customer?.listedCustomersPage;
  });
  const isGettingListedCustomersPage = useSelector(
    (state) => state.customer.isGettingListedCustomersPage
  );

  const customer = useSelector((state) =>
    customerId ? getCustomer(state, customerId) : undefined
  );
  const isGettingCustomer = useSelector(
    (state) => state.customer.isGettingCustomer
  );
  const isSettingDealCustomer = useSelector(
    (state) => state.deals.isSettingDealCustomer
  );

  useEffect(() => {
    if (customerId) {
      dispatch(customerActions.requestGetCustomer(customerId));
      onPersistCustomerSuccess?.();
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'onPersistCustomerSuccess', and 'dispatch'
  }, [customerId]);

  const primaryCustomerContactId = useMemo(() => {
    if (!customer) return;
    const primaryContact = getPrimaryContactFromCustomer(customer);
    return primaryContact?.id;
  }, [customer]);

  const selectedContactId = useMemo(() => {
    if (customerId) {
      return jobContactId || primaryCustomerContactId;
    }
  }, [customerId, jobContactId, primaryCustomerContactId]);

  const onSearchTermChange = useCallback((searchTerm: string) => {
    setGetListedCustomersRequestData(
      (getListedCustomersRequestData: GetListedCustomersRequestData) => {
        return {
          ...getListedCustomersRequestData,
          pageNumber: 1,
          searchTerm: searchTerm,
        };
      }
    );
  }, []);

  const renderCustomerContact = useCallback(
    (
      customerContact: CustomerContact,
      index: number,
      originalComponent: JSX.Element
    ) => {
      return (
        <div
          className="customer-contact-select-wrapper"
          key={`customer-contact-${index}`}
        >
          <RadioButton
            onClick={() =>
              onSelectContact && onSelectContact(customerContact.id)
            }
            isSelected={customerContact.id === selectedContactId}
          />
          {originalComponent}
        </div>
      );
    },
    [onSelectContact, selectedContactId]
  );

  const customerContactCard = useMemo(() => {
    return (
      <ContentPanel hasBoxShadow={false} className="customer-contact-card">
        <CustomerInformation
          customer={customer}
          hideAddresses={true}
          renderCustomerContact={renderCustomerContact}
        />
      </ContentPanel>
    );
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: onSelectCustomer
  }, [customer, onSelectCustomer, renderCustomerContact]);

  const renderOption = (
    option: Option,
    props: TypeToSearchRenderOptionProps
  ) => {
    return (
      <li
        {...props}
        className={clsx(
          'listed-job-customer-option flex-container flex-center',
          props.className
        )}
      >
        {option.id === -1 && <IconButton size={'xs'} icon="plus.3" />}
        {option.id === -2 && <UntitledIcon name="search-lg.3" />}
        <div className="listed-job-customer-name-email-wrapper">
          <span>{option.name}</span>
          {option.primaryContactEmailAddress && (
            <span className="listed-job-customer-email">
              - {option.primaryContactEmailAddress}
            </span>
          )}
        </div>
      </li>
    );
  };

  const handleOpenAdvancedSearchCustomerModal = () => {
    // TODO: Open customer search modal (with search term?)
    setShowSearchCustomerTableModal(true);
  };

  const addCustomerControl = useMemo(() => {
    let customerResults: Array<{
      id: ListedCustomer['customerId'];
      name: ListedCustomer['customerName'];
      primaryContactEmailAddress?: ListedCustomer['primaryContactEmailAddress'];
    }> = [];
    // only start showing customer results when started typing
    if (getListedCustomersRequestData.searchTerm) {
      customerResults = (listedCustomersPage?.items || []).map(
        (c: ListedCustomer) => ({
          id: c.customerId,
          name: c.customerName,
          primaryContactEmailAddress: c.primaryContactEmailAddress,
        })
      );
    }
    return (
      <TypeToSearchField
        styleVariant={InputStyleVariant.OUTSIZE}
        options={[
          ...customerResults,

          {
            id: -2,
            name: 'Advanced search',
          },
        ]}
        renderOption={renderOption}
        disableFilteringInComponent={true}
        isLoadingOptions={isGettingListedCustomersPage}
        onSelectOption={(option?: Option) => {
          if (option?.id === -2) {
            handleOpenAdvancedSearchCustomerModal();
          } else if (option?.id) {
            // selected existing customer - set onto job
            onSelectCustomer?.(option.id);
          }
        }}
        placeholder="Type here to search Customers"
        onSearchTermChange={onSearchTermChange}
      />
    );
  }, [
    getListedCustomersRequestData,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `handleOpenAdvancedSearchCustomerModal`, which changes every render
    handleOpenAdvancedSearchCustomerModal,
    onSelectCustomer,
    isGettingListedCustomersPage,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `renderOption`, which changes every render
    renderOption,
    onSearchTermChange,
    listedCustomersPage,
  ]);

  return (
    <EditJobSection
      title="Customer"
      description={!customer ? <p>Add a Customer</p> : undefined}
      className="job-customer-control"
    >
      {(isSettingDealCustomer || (isGettingCustomer && !customer)) && (
        <div className="loader-container">
          <MiniLoader />
        </div>
      )}

      {!isSettingDealCustomer &&
        (customer
          ? // Customer and contact card
            customerContactCard
          : // Add customer control
            !isGettingCustomer && addCustomerControl)}

      {/* search customer modal */}
      {canSelectCustomer && (
        <SelectCustomerModal
          isOpen={showSearchCustomerTableModal}
          onClose={() => setShowSearchCustomerTableModal(false)}
          onSelectCustomer={(customerId: number) => {
            setShowSearchCustomerTableModal(false);
            onSelectCustomer?.(customerId);
          }}
        />
      )}
    </EditJobSection>
  );
};

export default JobCustomerControl;
